import React from 'react';
import { Autocomplete, Box, Chip, createFilterOptions, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Country } from 'country-state-city';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import { EducationHistoryEntry, InstitutionType } from '../../../types/user';
import {
	EducationJurisdiction,
	findOngoingEntry,
	INSTITUTION_TYPES,
	institutionTypeLabel,
	resolveEducationJurisdiction,
	subdivisionNoun
} from '../../../utils/educationHistory';
import EducationTimeline from './EducationTimeline';
import PillButton from '../atoms/PillButton';
import { useSchoolDirectory } from '../../../hooks/useSchoolDirectory';
import { DirectorySchool, SEARCH_MIN_CHARS } from '../../../redux/slices/schoolDirectory';

const MAX_ENTRIES = 20;
const MAX_INSTITUTION_LENGTH = 120;

// Local, typo-tolerant filtering over the (small) scoped list; the limit keeps
// the popup light without a virtualisation dependency. No keystroke leaves the
// device — the scope was fetched once and is filtered in memory.
const schoolFilter = createFilterOptions<DirectorySchool>({
	limit: 100,
	ignoreAccents: true,
	matchFrom: 'any',
	trim: true,
	stringify: (option) => `${option.name} ${option.city ?? ''}`
});

/**
 * Canonical education-history editor (PTEC-517).
 *
 * Adding a school is pill-first: pick the institution type from
 * jurisdiction-localised pills (UK "Secondary school", US "High school", …),
 * name it, optionally year it — each on its own row. Added institutions render
 * as a TIMELINE, most recent first; the derived "most recent school attended"
 * is highlighted since it is the only school detail a Lead-Share consent would
 * disclose and share. Stored values are jurisdiction-neutral canonical codes.
 */
export const EducationHistoryEditor = ({
	value,
	onChange,
	jurisdiction = 'INTL',
	defaultCountryIso = ''
}: {
	value: EducationHistoryEntry[];
	onChange: (next: EducationHistoryEntry[]) => void;
	jurisdiction?: EducationJurisdiction;
	/** ISO code pre-selected as "country of study" (from the profile address). */
	defaultCountryIso?: string;
}) => {
	const [type, setType] = React.useState<InstitutionType | null>(null);
	const [institution, setInstitution] = React.useState('');
	const [endYear, setEndYear] = React.useState('');
	const [error, setError] = React.useState('');

	// School directory (PTEC-576): country of study is the jurisdiction KEY.
	// Flag off, or a country without a directory (the server's Null Object),
	// leaves the free-text field exactly as before.
	const [studyCountry, setStudyCountry] = React.useState(defaultCountryIso.toUpperCase());
	const [subdivision, setSubdivision] = React.useState('');

	/**
	 * Debounced copy of what has been typed (PTEC-643). Every keystroke past three
	 * characters is a partition query against the token index, and this is a
	 * consumer field, so the delay is not cosmetic.
	 */
	const [query, setQuery] = React.useState('');
	React.useEffect(() => {
		const timer = setTimeout(() => setQuery(institution), 300);
		return () => clearTimeout(timer);
	}, [institution]);

	/**
	 * The directory record behind the current input, when one was chosen. Held so
	 * `add` can store its id, and cleared the moment the text stops matching —
	 * the field is freeSolo, so a user can pick "Lincoln High School" and then edit
	 * it, and storing the original id against edited text would be worse than
	 * storing no id at all.
	 */
	const [picked, setPicked] = React.useState<DirectorySchool | null>(null);

	const directory = useSchoolDirectory(
		studyCountry || undefined,
		subdivision || undefined,
		type ?? undefined,
		query || undefined
	);
	/**
	 * A country with a directory is enough. Requiring a state and a type first is
	 * what made this undiscoverable — they are narrowing filters now, not gates.
	 */
	const directoryActive = directory.supported;
	const countries = React.useMemo(() => Country.getAllCountries(), []);

	// The stage vocabulary follows the COUNTRY OF STUDY when one is chosen (a
	// US school is a "High school", whatever the profile's home jurisdiction);
	// the profile jurisdiction is only the fallback.
	const labelJurisdiction = studyCountry
		? resolveEducationJurisdiction({ isoCountryCode: studyCountry })
		: jurisdiction;

	const add = () => {
		const name = institution.trim();
		if (!name) {
			setError('Enter a school or institution name.');
			return;
		}
		if (name.length > MAX_INSTITUTION_LENGTH) {
			setError(`Must be ${MAX_INSTITUTION_LENGTH} characters or fewer.`);
			return;
		}
		const currentYear = new Date().getFullYear();
		let year: number | undefined;
		if (endYear.trim() !== '') {
			year = Number(endYear.trim());
			if (!Number.isInteger(year) || year < 1900 || year > currentYear + 10) {
				setError(`Year must be between 1900 and ${currentYear + 10}.`);
				return;
			}
		}
		if (value.length >= MAX_ENTRIES) {
			setError(`You can list up to ${MAX_ENTRIES} schools.`);
			return;
		}
		if (year === undefined) {
			const ongoing = findOngoingEntry(value);
			if (ongoing) {
				setError(
					`Only one school can be ongoing — “${ongoing.institution}” already is. Add a finish year to one of them.`
				);
				return;
			}
		}
		if (value.some((entry) => entry.institution.toLowerCase() === name.toLowerCase() && entry.endYear === year)) {
			setError('That school is already listed.');
			return;
		}
		const entry: EducationHistoryEntry = { institution: name };
		// Only when the text is still exactly what was chosen. Anything typed, or
		// edited after choosing, is stored as a plain name with no id — which is the
		// signal that it is not canonical.
		if (picked && picked.name === name) {
			entry.sourceId = picked.sourceId;
			if (picked.state) {
				entry.subdivision = picked.state;
			}
		}
		if (type) {
			entry.type = type;
		}
		if (studyCountry) {
			entry.country = studyCountry;
		}
		if (year !== undefined) {
			entry.endYear = year;
		}
		onChange([...value, entry]);
		setType(null);
		setPicked(null);
		setInstitution('');
		setEndYear('');
		setError('');
	};

	const remove = (entry: EducationHistoryEntry) => onChange(value.filter((candidate) => candidate !== entry));

	const submitOnEnter = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			add();
		}
	};

	return (
		<Box>
			<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
				Education history
			</Typography>
			<Typography variant="caption" sx={{ color: 'var(--gray-1)', display: 'block', mb: 1.5 }}>
				Optional. Your most recent school is highlighted — it&apos;s the only school detail a partner could
				receive, and only if you explicitly agree to share it.
			</Typography>

			<Box sx={{ mb: 1 }}>
				<EducationTimeline entries={value} jurisdiction={jurisdiction} onRemove={remove} />
			</Box>

			{directory.enabled && (
				<Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
					<TextField
						select
						size="small"
						label="Country of study"
						value={studyCountry}
						onChange={(event) => {
							setStudyCountry(event.target.value);
							setSubdivision('');
						}}
						sx={{ minWidth: 220 }}
					>
						{countries.map((option) => (
							<MenuItem key={option.isoCode} value={option.isoCode}>
								{option.flag} {option.name}
							</MenuItem>
						))}
					</TextField>
					{directory.supported && (
						<TextField
							select
							size="small"
							label={subdivisionNoun(studyCountry).singular}
							value={subdivision}
							onChange={(event) => setSubdivision(event.target.value)}
							sx={{ minWidth: 200 }}
						>
							{directory.subdivisions.map((option) => (
								<MenuItem key={option.code} value={option.code}>
									{option.name}
								</MenuItem>
							))}
						</TextField>
					)}
				</Stack>
			)}
			<Typography variant="caption" sx={{ color: 'var(--gray-1)', display: 'block', mb: 0.75 }}>
				Add a school — pick the type:
			</Typography>
			<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
				{INSTITUTION_TYPES.map((candidate) => {
					const on = type === candidate;
					return (
						<Chip
							key={candidate}
							size="small"
							label={institutionTypeLabel(candidate, labelJurisdiction)}
							variant={on ? 'filled' : 'outlined'}
							color={on ? 'primary' : 'default'}
							onClick={() => setType(on ? null : candidate)}
							data-testid={`institution-type-${candidate}`}
						/>
					);
				})}
			</Stack>
			{directoryActive ? (
				<Autocomplete
					freeSolo
					options={directory.schools}
					filterOptions={schoolFilter}
					loading={directory.loadingSchools}
					getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
					renderOption={(props, option) => {
						// City AND state (PTEC-643). A cross-state search returns several
						// schools of the same name and city alone does not separate them —
						// "Lincoln High School, Portland" is still ambiguous without OR.
						const where = [option.city, option.state].filter(Boolean).join(', ');
						return (
							<li {...props} key={option.sourceId ?? `${option.name}#${option.city ?? ''}`}>
								<Box>
									{option.name}
									{where && (
										<Typography
											component="span"
											variant="caption"
											sx={{ color: 'var(--gray-1)', ml: 0.75 }}
										>
											{where}
										</Typography>
									)}
								</Box>
							</li>
						);
					}}
					inputValue={institution}
					onInputChange={(_, next) => {
						setInstitution(next);
						// Typing away from the chosen school drops its id with it.
						setPicked((current) => (current && current.name === next ? current : null));
					}}
					onChange={(_, selected) => {
						if (typeof selected === 'string' || selected === null) {
							setInstitution(selected ?? '');
							setPicked(null);
							return;
						}
						setInstitution(selected.name);
						setPicked(selected);
						// A directory school knows its own stage, so offer it rather than
						// making the user restate it. Still editable afterwards.
						if (!type && selected.type) {
							setType(selected.type as InstitutionType);
						}
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							size="small"
							label="School or institution"
							onKeyDown={submitOnEnter}
							error={error !== ''}
							helperText={
								error ||
								`Type ${SEARCH_MIN_CHARS}+ characters to search the directory, or enter any school name.`
							}
						/>
					)}
					sx={{ mb: 1.5 }}
				/>
			) : (
				<TextField
					fullWidth
					size="small"
					label="School or institution"
					value={institution}
					onChange={(event) => setInstitution(event.target.value)}
					onKeyDown={submitOnEnter}
					error={error !== ''}
					helperText={error || undefined}
					sx={{ mb: 1.5 }}
				/>
			)}
			<Stack direction="row" spacing={1} alignItems="center">
				<TextField
					size="small"
					label="Year finished"
					placeholder="Ongoing"
					value={endYear}
					onChange={(event) => setEndYear(event.target.value)}
					onKeyDown={submitOnEnter}
					inputProps={{ inputMode: 'numeric', maxLength: 4 }}
					sx={{ width: 140 }}
				/>
				<PillButton onClick={add} startIcon={<SchoolRounded />}>
					Add school
				</PillButton>
			</Stack>
		</Box>
	);
};

export default EducationHistoryEditor;
